<script setup lang="ts">
import {computed, ref} from "vue";
import DialogCloseIcon from "@/assets/editViewIcons/DialogCloseIcon.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import Translator from "@/utils/common/Translator.ts";
import {copyToClipboard, Dialog} from "quasar";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import FieldInfoUtils from "@/utils/common/FieldInfoUtils.ts";
import DMProperty = namaTypes.DMProperty;
import DMDetail = namaTypes.DMDetail;
import GUIActionPlaceholder = namaTypes.GUIActionPlaceholder;

const props = defineProps<{
  property?: DMProperty,
  parent?: DMDetail,
  entityType: string,
  enumType?: { enumTypeName: string, allowedValues: string },
  action?: GUIActionPlaceholder
}>()
const property = props.property
const parent = props.parent
const entityType = props.entityType
const enumType = props.enumType
const action = props.action
const fieldId = property?.fullName
const shouldShowCopiedIcon = ref({});
const dialog = ref(false);
const button = computed(() => (itemId: string, isLink?: boolean) => {
  if (isLink)
    return {icon: 'launch', text: "open"}
  return shouldShowCopiedIcon.value[itemId] ? {icon: "nm-clipboard-check", text: "Copied"} : {icon: "nm-copy-to-clipboard", text: "Copy"}
})

function copy(itemId: string, text?: string, event?: MouseEvent) {
  if (!text)
    return;
  if (event) {
    const h1 = (event.target as HTMLElement).closest("h1[data-target-heading]") as HTMLHeadingElement | undefined
    if (!h1)
      return;
    const range = document.createRange();
    range.selectNodeContents(h1);
    const selection = window.getSelection();
    if (!selection)
      return;
    selection.removeAllRanges();
    selection.addRange(range);
  }
  copyToClipboard(text)
  shouldShowCopiedIcon.value[itemId] = true
  setTimeout(() => {
    shouldShowCopiedIcon.value[itemId] = false
  }, 2000)
}

function handleClick(itemId: string, value?: string, isLink?: boolean, event?: MouseEvent) {
  if (!value)
    return;
  if (!isLink)
    return copy(itemId, value, event)
  if (!event)
    window.open(value, "_blank")
}

function getItems(): { label: string, value?: string, isLink?: boolean, showInTextArea?: boolean }[] {
  if (action) {
    const isCustomAction = (action.customization && !action.customization.empty)
    const base = [
      {label: "ID (System)", value: action.id},
      {label: "Post Action Type", value: isCustomAction ? 'Custom Post Action' : 'System Post Action'}
    ]
    const customization = isCustomAction ? [
      {
        label: "Entity Flow Link",
        value: PlaceUtils.editLink(BSCEntities.EntityFlow, action.customization?.entityFlowId),
        isLink: true
      },
      {
        label: "Gui Post Action Link",
        value: PlaceUtils.editLink(BSCEntities.GUIPostActions, action.customization?.guiPostActionsId),
        isLink: true
      },
      {
        label: "Notification Link",
        value: PlaceUtils.editLink(BSCEntities.NotificationDefinition, action.customization?.notificationId),
        isLink: true
      },
      {
        label: "Query",
        value: action.customization?.query,
        showInTextArea: true
      },
      {
        label: "Url Template",
        value: action.customization?.urlTemplate,
        showInTextArea: true
      },
      {
        label: "Launch Type",
        value: action.customization?.reportInfo?.launchType
      },
      {
        label: "Report Link",
        value: PlaceUtils.editLink(BSCEntities.ReportDefinition, action.customization?.reportInfo?.reportId),
        isLink: true
      },
      {
        label: "Selected Records Only",
        value: action.customization?.reportInfo?.selectedRecordsOnly ? 'yes' : ''
      },
      {
        label: "ShowIn",
        value: action.customization?.reportInfo?.showIn
      }
    ] : [];
    if (action.customization?.reportInfo) {
      for (let p = 1; p <= 10; p++) {
        let info = action.customization.reportInfo["p" + p] as namaTypes.PMap;
        if (!info)
          continue;
        if (!info.field && !info.parameter)
          continue;
        customization.push({label: "Parameter " + p, value: `${info.field ?? ""} - ${info.parameter ?? ""}`})
      }
    }
    return [...base, ...customization]
  } else if (fieldId)
    return [
      {label: "ID (System)", value: fieldId},
      {label: "Table (SQL)", value: parent?.tableName},
      {label: "Column (SQL)", value: property?.columnName},
      {label: "References Table (SQL)", value: property?.referenceTo},
      {label: "Arabic (System)", value: Translator.translateIdAr(entityType + "." + fieldId)},
      {label: "English (System)", value: Translator.translateIdEn(entityType + "." + fieldId)},
      {label: "Allowed Values (System)", value: enumType?.allowedValues},
      {label: "Enum Type Name (System)", value: enumType?.enumTypeName},
      {label: "ColumnName (Report)", value: (property && property.columnName) ? `$F{${property.columnName}}` : undefined},
      {
        label: "NamaRep.name (Report)",
        value: `NamaRep.name("${Translator.translateIdAr(entityType + "." + fieldId)}","${Translator.translateIdEn(entityType + "." + fieldId)}")`
      },
      {label: "NamaRep.translate (Report)", value: `NamaRep.translate("${entityType + "." + fieldId}")`},
      {label: "Entity Table (Report)", value: entityType},
      {label: "Join Column (Line)(Report)", value: parent?.joinColumn},
      {
        label: "Detail Line Left Join (Report)",
        value: (parent && parent.tableName && parent.joinColumn) ? `LEFT JOIN ${parent.tableName} on ${parent.tableName}.${parent.joinColumn} = ${entityType}.id` : undefined
      },
      {
        label: "Reference Table Left Join (Report)",
        value: (property && property.columnName) ? `LEFT JOIN ${property.referenceTo === "" ? "EntitySystemEntry" : property.referenceTo} on ${property.referenceTo === "" ? "EntitySystemEntry" : property.referenceTo}.targetId = ${entityType}.${property.columnName}` : undefined
      }
    ]
  return []
}

async function onEditEnumTranslationsClick() {
  let allowedValues = await NamaCommon.namaFetch(NamaURLS.FETCH_ENUM_ALLOWED_VALUES, {params: {enumTypeName: enumType?.enumTypeName}})
  Dialog.create({
    component: FieldInfoUtils.UpdateTranslationsDialogIndirectComponent,
    componentProps: {allowedValues, enumTypeName: enumType?.enumTypeName}
  })
}
</script>
<template>
  <q-dialog v-model="dialog" transition-hide="jump-down" transition-show="jump-up">
    <q-card
      class="tw-min-w-[300px] !tw-max-w-[700px] tw-flex tw-flex-col !tw-max-h-[85vh] tw-relative dark:tw-bg-namaBackground-deep-dark tw-rounded-md"
      dir="ltr">
      <q-toolbar
        class="tw-flex tw-shrink-0 tw-sticky tw-left-0 tw-translate-x-0 !tw-rounded-none tw-items-center tw-px-4 tw-justify-between tw-bg-primary-light tw-text-white">
        <q-toolbar-title class="tw-flex tw-items-center tw-gap-2">
          <NamaIcon default-icon="nm-information" class="active-white"/>
          <span v-text="' Field Info(Double Click to Copy)'"/>
        </q-toolbar-title>
        <q-avatar square class="tw-w-5 tw-cursor-pointer" @click="dialog=false">
          <NamaIcon default-icon="close" class="active-white"/>
        </q-avatar>
      </q-toolbar>
      <q-card-section class="!tw-p-2 tw-overflow-x-hidden tw-max-w-[inherit] tw-w-fit tw-max-h-[inherit] tw-overflow-y-auto">
        <template v-for="{label,value,isLink,showInTextArea} in getItems()" :key="label">
          <li class="tw-flex tw-my-2 tw-group tw-ring-1 tw-rounded-md tw-ring-black/20 dark:tw-ring-white/20"
              :id="label"
              v-if="value">
            <h2 v-text="label"
                class="tw-min-w-[250px] group-odd:tw-bg-zinc-200 group-even:tw-bg-zinc-100
                 dark:group-odd:tw-bg-namaBackground-bright-dark dark:group-even:tw-bg-[#414141] tw-h-auto tw-leading-none tw-font-semibold tw-items-center tw-rounded-s-md dark:tw-text-white
                 tw-py-1 tw-px-3 tw-flex"/>
            <div :class="[{'tw-min-w-[250px]':action}]"
                 class="tw-w-full tw-overflow-hidden tw-max-w-[inherit] tw-flex tw-pe-2 tw-items-center tw-rounded-e-md tw-relative"
                 @dblclick="handleClick(label,value,isLink,$event)">
              <a v-if="isLink" :href="value" target="_blank">
                <h1
                  v-text="'Link'" class="tw-leading-none tw-overflow-x-auto tw-font-semibold  tw-py-3 tw-ps-3"/>
              </a>
              <div v-else class="tw-overflow-x-auto">
                <h1
                  v-text="value" data-target-heading
                  :class="[showInTextArea?'tw-whitespace-pre':'tw-whitespace-nowrap']"
                  class="tw-leading-none tw-w-full tw-font-semibold tw-text-namaFontColor-light dark:tw-text-white/95 tw-py-3 tw-ps-3 tw-pe-8"/>
              </div>
              <q-btn size="sm" @click="handleClick(label,value,isLink)" flat dense
                     class="tw-opacity-0 tw-bg-gray-200 dark:tw-bg-namaBackground-deep-dark tw-rounded-md tw-w-fit group-hover:tw-opacity-100 tw-absolute
                tw-top-1/2 -tw-translate-y-1/2"
                     v-if="value"
                     style="right:.25rem"
              >
                <div class="tw-flex tw-gap-1 tw-items-center">
                  <NamaIcon :default-icon="button(label,isLink).icon"
                            class="tw-size-4 !tw-text-[16px]"/>
                  <span v-text="button(label,isLink).text"
                        class="tw-capitalize tw-text-sm tw-text-namaFontColor-light/50 dark:tw-text-white/50 "/>
                </div>
              </q-btn>
            </div>
          </li>
        </template>
      </q-card-section>
      <div v-if="enumType?.enumTypeName" class="tw-pt-2 tw-grid">
        <q-separator class="tw-bg-black/20 dark:tw-bg-white/20"/>
        <q-btn
          unelevated color="primary" no-caps
          @click="onEditEnumTranslationsClick"
          class="tw-shrink-0 tw-rounded-md tw-shadow-sm nama-light-shadow tw-w-fit tw-mx-auto tw-my-2">
          <div class="tw-flex tw-gap-2 tw-items-center">
            <span v-text="'Edit Enum Translations'"/>
            <NamaIcon default-icon="nm-edit" class="tw-size-5 active-white"/>
          </div>
        </q-btn>
      </div>
    </q-card>
  </q-dialog>
</template>
